import { handleApiError } from "./handleApiError";
import instance from "./index";

type TGetAllJobs = {
  page?: number;
  limit?: number;
  orderBy?: string;
};

type TPostJob = {
  name: string;
  description: string;
  categoryId: string;
  repeats: boolean;
  repeatInterval: string;
  repeatEach: number;
  repeatUntil?: string;
  weekdays?: string[];
  visitCount?: number;
  startDate: string;
  endDate: string;
  address: string;
  latitude: number;
  longitude: number;
};

type TGetPriceEstimate = {
  categoryId: string;
  startDate: string;
  endDate: string;
};

export const getIncompleteQuestionnaire = async () => {
  try {
    const response = await instance.get(`/job/incomplete-questionnaire`);
    return response.data;
  } catch (error: unknown) {
    handleApiError(error);
  }
};

export const getAllJobs = async ({
  page = 1,
  limit = 12,
  orderBy = "startDate-asc",
}: TGetAllJobs) => {
  try {
    const response = await instance.get(`/job/my`, {
      params: {
        orderBy,
        page,
        limit,
      },
    });
    return response.data;
  } catch (error: unknown) {
    handleApiError(error);
  }
};

export const addJob = async (data: TPostJob) => {
  try {
    const response = await instance.post(`/job`, data);
    return response.data;
  } catch (error: unknown) {
    handleApiError(error);
  }
};

export const getJob = async ({ id }: { id: string }) => {
  try {
    const response = await instance.get(`/job/${id}`);
    return response.data;
  } catch (error: unknown) {
    handleApiError(error);
  }
};

export const getJobCategories = async () => {
  try {
    const response = await instance.get(`/job/categories`);

    return response.data;
  } catch (error: unknown) {
    handleApiError(error);
  }
};

export const getPriceEstimate = async ({
  categoryId,
  startDate,
  endDate,
}: TGetPriceEstimate) => {
  try {
    const response = await instance.get(`/price-estimate`, {
      params: {
        categoryId,
        startDate,
        endDate,
      },
    });
    return response.data;
  } catch (error: unknown) {
    handleApiError(error);
  }
};

export const getAdminCategories = async () => {
  try {
    const response = await instance.get(`/job/categories/admin`);
    return response.data;
  } catch (error: unknown) {
    handleApiError(error);
  }
};

export const updateCategoryRates = async ({
  id,
  clientHourly,
  assistantHourly,
}: {
  id: string;
  clientHourly?: number;
  assistantHourly?: number;
}) => {
  try {
    const response = await instance.patch(`/job/categories/${id}/rates`, {
      clientHourly,
      assistantHourly,
    });
    return response.data;
  } catch (error: unknown) {
    handleApiError(error);
  }
};

export const bulkUpdateCategoryRates = async ({
  categoryIds,
  clientHourly,
  assistantHourly,
}: {
  categoryIds: string[];
  clientHourly?: number;
  assistantHourly?: number;
}) => {
  try {
    const response = await instance.patch(`/job/categories/rates/bulk`, {
      categoryIds,
      clientHourly,
      assistantHourly,
    });
    return response.data;
  } catch (error: unknown) {
    handleApiError(error);
  }
};

export type ClientSite = {
  id: string;
  name: string;
  address: string;
  jobCount: number;
  categoryNames: string[];
  hasQuestionnaireNotes: boolean;
  lastJobDate: string | null;
  possibleDuplicateSiteIds: string[];
  jobs: Array<{
    id: string;
    name: string;
    categoryName: string | null;
    startDate: string;
  }>;
};

export const getClientSites = async (userId: string) => {
  try {
    const response = await instance.get(`/job/admin/clients/${userId}/sites`);
    return response.data as { clientId: string; userId: string; sites: ClientSite[] };
  } catch (error: unknown) {
    handleApiError(error);
  }
};

export const mergeClientSites = async ({
  userId,
  keepSiteId,
  mergeSiteIds,
}: {
  userId: string;
  keepSiteId: string;
  mergeSiteIds: string[];
}) => {
  try {
    const response = await instance.post(`/job/admin/clients/${userId}/sites/merge`, {
      keepSiteId,
      mergeSiteIds,
    });
    return response.data as {
      keepSiteId: string;
      mergedSiteIds: string[];
      jobsMoved: number;
      notesUpdated: boolean;
    };
  } catch (error: unknown) {
    handleApiError(error);
  }
};
